import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {TypePickerDialog} from '@/components/shared/TypePickerDialog'
import {Plus} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Icon} from '@/components/shared/Icon'
import {permissions} from '@/lib/permissions'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {TargetSystemControllerSystemsEndpoint} from '@/types/endpoint-contracts'
import {omitContractFilters} from '@/types/endpoint-contract'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {routes} from '@/lib/routes'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'

export interface PromotedSystemListTheme {
    title: string
    icon?: ConceptKey
    countLabel?: (params: {count: number}) => string
    createLabel?: string
}

export interface SystemsProps {
    /** Pinned systemType — hides the type filter and re-themes the page chrome (issue 301). */
    lockedType?: string
    /** Optional re-themed page chrome for the promoted by-type route. */
    themedAs?: PromotedSystemListTheme
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

const SYSTEM_TYPE_PARAM = 'systemType'

export function Systems({lockedType, themedAs, embedded, ownedByMe, emptyStateTitle}: SystemsProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {systemTypes, environmentTypes: environments} = useAppConfig()
    const [typePickerOpen, setTypePickerOpen] = useState(false)
    usePageTitle(i18n.systems.pageTitle)

    const effectiveContract = useMemo(
        () => lockedType
            ? omitContractFilters(TargetSystemControllerSystemsEndpoint, [SYSTEM_TYPE_PARAM])
            : TargetSystemControllerSystemsEndpoint,
        [lockedType],
    )

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: TargetSystemControllerSystemsEndpoint,
        sortable: true,
        defaultSortBy: TargetSystemControllerSystemsEndpoint.sort.DERIVED_DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const headerTitle = themedAs?.title ?? i18n.systems.allSystems

    const {listParams, canCreate, title, emptyMessage} = useEmbeddedListProps({
        contract: TargetSystemControllerSystemsEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: headerTitle,
        createPermission: permissions.systems.create,
    })

    const requestParams = useMemo(() => {
        const params = {...listParams}
        if (lockedType) params[SYSTEM_TYPE_PARAM] = lockedType
        return params
    }, [listParams, lockedType])

    const {data: systemsData} = useQuery({
        queryKey: ['systems', queryString, page, pageSize, lockedType, ownedByMe],
        queryFn: () => services.systems.list(requestParams),
    })

    const systems = systemsData?.content || []
    const totalItems = systemsData?.totalElements || 0
    const totalPages = systemsData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const headerIcon = themedAs?.icon ? CONCEPT_ICON[themedAs.icon] : CONCEPT_ICON.system
    const countLabel = themedAs?.countLabel
        ? themedAs.countLabel({count: totalItems})
        : i18n.systems.systemCount({count: totalItems})

    return (
        <PageLayout
            variant="list"
            icon={headerIcon}
            title={title}
            count={totalItems}
            description={`${countLabel}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="systems-list"
            embedded={embedded}
            actions={canCreate ? (
                <Button
                    size="sm"
                    onClick={() => setTypePickerOpen(true)}
                    data-testid="btn-new-system"
                >
                    <Icon icon={Plus} className="mr-1"/>
                    {themedAs?.createLabel ?? i18n.systems.createSystem}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={effectiveContract}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.systems.searchByName}
                    lockedDiscriminatorValues={lockedType ? [lockedType] : undefined}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                        <TableHead data-testid="col-environment">{i18n.systems.environment}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {systems.map((system) => (
                        <ClickableTableRow key={system.id} to={routes.systems.detail(system.id)}>
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.systems.detail(system.id)}
                                    testId={`link-system-detail-${system.id}`}
                                    stopPropagation
                                >
                                    {system.name}
                                </TextLink>
                                {system.description && (
                                    <MutedText size="sm" className="block">
                                        {system.description}
                                    </MutedText>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(systemTypes, system.systemType)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(environments, system.environment)}
                                </Badge>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {systems.length === 0 && (
                        <TableEmptyState
                            colSpan={3}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.systems.noSystemsMatch}
                            emptyMessage={emptyMessage ?? i18n.systems.noSystemsFound}
                        />
                    )}
                </TableBody>
            </Table>
            <TypePickerDialog
                open={typePickerOpen}
                onOpenChange={setTypePickerOpen}
                title={i18n.systems.pickType}
                description={i18n.systems.pickTypePrompt}
                types={systemTypes}
                testIdPrefix="pick-system-type"
                onPick={(code) => {
                    setTypePickerOpen(false)
                    navigate(routes.systems.createByType(code))
                }}
            />
        </PageLayout>
    )
}
